import { removeClass, addClass, hide, show, includes } from "../../common";
import { FilterParamItem } from "../filter-param-items";

export default class CheckboxOption {
    private defaultChildNumberDisplayed = 10;
    private input: HTMLInputElement;
    private sublevelContainer: HTMLElement;
    private onSelectionChanged: () => void;
    protected onSelectOnly: (listId: string) => void;
    private showMoreText: string; 
    private showLessText: string;
    private showMoreLinkElement: HTMLElement;
    private linkElement: HTMLElement;
    private selectedLinkClass: string = 'selected-fake-link';
    
    private _listId: string;
    get listId(): string {
        return this._listId;
    }

    private _sortValue: number;
    get sortValue(): number {
        return this._sortValue;
    }

    protected _children: CheckboxOption[] = [];
    get children(): CheckboxOption[] {
        return this._children.slice();
    }

    private _name: string;
    get name(): string{
        return this._name;
    }
    
    private _baseElement: HTMLElement;
    get baseElement(): HTMLElement{
        return this._baseElement;
    }

    get isSelected(): boolean {
        return this.input.checked;
    }

    public static createOptionElement(optionItem: FilterParamItem): HTMLElement{
        const div = document.createElement('div');
        div.classList.add('checkbox-value');
        div.setAttribute('data-wrapper-list-id', optionItem.ListId.toString());
        div.setAttribute('data-sort', optionItem.Sort.toString());
        
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.classList.add('checkbox-filter-input');
        input.classList.add('top-input');
        input.setAttribute('data-component-part', 'top-level-item');
        input.setAttribute('data-list-id', optionItem.ListId.toString());
        input.setAttribute('name', optionItem.Name);
        label.appendChild(input);
        const span = document.createElement('span');
        span.dataset.linkListId = optionItem.ListId.toString();
        span.classList.add('checkbox-fake-link');
        span.innerText = optionItem.Name;
        label.appendChild(span);
        div.appendChild(label);

        return div;
    }


    constructor(baseElement: HTMLElement, onSelectionChanged: () => void, onSelectOnly: (listId: string) => void, showMoreText: string, showLessText: string){
        this._baseElement = baseElement;
        this.onSelectionChanged = onSelectionChanged;
        this.onSelectOnly = onSelectOnly;
        this.showMoreText = showMoreText;
        this.showLessText = showLessText;
        this._listId = this._baseElement.dataset.wrapperListId;
        this._sortValue = Number(this._baseElement.dataset.sort);
        this.input = this._baseElement.querySelector(`[data-list-id="${this._listId}"]`) as HTMLInputElement;
        this._name = this.input.name;
        this.input.addEventListener('change', () => this.selectionChanged());

        this.linkElement = this.baseElement.querySelector('[data-link-list-id]');
        if (this.linkElement) {
            this.linkElement.addEventListener('click', t => { t.preventDefault(); this.onSelectOnly(this.listId); });
        }

        this.initChildren();
    }

    private initChildren(): void {
        this.sublevelContainer = this._baseElement.querySelector(':scope > .level-container');
        if (!this.sublevelContainer) return;

        const childElements = [...this.sublevelContainer.querySelectorAll(':scope > .checkbox-value-child')];
        this._children = childElements.map(el => 
            new CheckboxOption(el as HTMLElement, this.onSelectionChanged, this.onSelectOnly, this.showMoreText, this.showLessText)
        );

        if (this._children.length > this.defaultChildNumberDisplayed) {
            this.showMoreLinkElement = this.createShowMoreLink();
            this.sublevelContainer.appendChild(this.showMoreLinkElement);
        }
    }

    public select(): void {
        this.input.checked = true;
    }

    public unselect(): void {
        this.input.checked = false;
    }
    public selectByIds(ids: string[]): void {
        if(!includes(ids, this.listId)) {
            return;
        }
        
        this.select();

        if (this.sublevelContainer) {
            show(this.sublevelContainer);
        }
        this.children.forEach(ch => ch.selectByIds(ids));
        this.showDefaultChildren();
    }

    public clear(): void {
        this.unselect();
        this.children.forEach(o => o.clear());
        if(this.sublevelContainer){
            hide(this.sublevelContainer);
        }
    }

    public unselectValue(listId: string): boolean {
        if(this.listId === listId && this.isSelected){
            this.unselect();
            return true;
        }
        for (let index = 0; index < this.children.length; index++) {
            const child = this.children[index];
            if(child.unselectValue(listId)){
                return true;
            }
        }
        return false;
    }

    public getSelectedValues(): CheckboxOption[] {
        let result: CheckboxOption[] = this.isSelected ? [ this ] : [];
        this.children.forEach(o => result = result.concat(o.getSelectedValues()));
        return result;
    }

    public hasSelectedChildren(): boolean {
        return this.children.some(ch => ch.isSelected) || this.children.some(ch => ch.hasSelectedChildren());
    }

    public selectOnlyValue(listId: string): boolean {
        for (const child of this.children) {
            if (child.selectOnlyValue(listId)) {
                this.select();
                if (this.sublevelContainer) {
                    show(this.sublevelContainer);
                }
                this.showDefaultChildren();
                return true;
            }
        }

        if (this.listId === listId) {
            this.select();
            this.onSelected();
            return true;
        }

        return false;
    }


    public updateItems(items: FilterParamItem[]){
        const item = items.find(i => i.ListId.toString() === this.listId);
        if(!item){
            this.hide();
            return;
        }
        
        this.show();

        if(this.isSelected){
            this.children?.forEach(ch => ch.updateItems(item.FilterValues));
        } else {
            this.children?.forEach(ch => ch.hide());
        }
        
        this.setShowMore(this.isShowMoreExpanded());
    }

    public hide(): void{
        hide(this.baseElement);
    }
    
    public show(): void{
        show(this.baseElement);
    }

    protected selectionChanged(): void {
        if(this.isSelected){
            this.onSelected();
        } else {
            this.onUnselected();
        }
        this.onSelectionChanged();
    }

    protected onSelected(): void {
        if (this.linkElement) {
            addClass(this.linkElement, this.selectedLinkClass);
        }
        if (this.sublevelContainer) {
            show(this.sublevelContainer);
        }
        this.children.forEach(ch => ch.select());
        this.children.forEach(ch => ch.onSelected());
        if (this.isShowMoreExpanded() || this._children.length <= this.defaultChildNumberDisplayed) {
            this.showAllSubvalues();
        } else {
            this.showDefaultChildren();
        }
    }

    protected onUnselected(): void {
        if (this.linkElement) {
            removeClass(this.linkElement, this.selectedLinkClass);
        }
        this.children.forEach(ch => ch.unselect());
        this.children.forEach(ch => ch.onUnselected());
        if (this.sublevelContainer) {
            hide(this.sublevelContainer);
        }
    }

    private createShowMoreLink(): HTMLElement {
        const element = document.createElement('div');
        element.dataset.toggleState = 'collapsed';
        element.dataset.dataListId = this.listId;
        element.classList.add('show-more-link');
        element.innerHTML = this.showMoreText;
        element.addEventListener('click', () => this.toggleShowMore());
        return element;
    }
    

    private toggleShowMore() {
        this.setShowMore(!this.isShowMoreExpanded());
    }

    private isShowMoreExpanded(): boolean{
        return this.showMoreLinkElement && this.showMoreLinkElement.dataset.toggleState === 'expanded';
    }

    private setShowMore(expanded: boolean){
        if(!this.showMoreLinkElement)
            return;

        if(expanded){
            this.showAllSubvalues();
            this.showMoreLinkElement.dataset.toggleState = 'expanded';
            this.showMoreLinkElement.classList.remove('show-more-link');
            this.showMoreLinkElement.classList.add('show-less-link');
            this.showMoreLinkElement.innerHTML = this.showLessText;
        } else {
            this.hideSomeSubValues();
            this.showMoreLinkElement.dataset.toggleState = 'collapsed';
            this.showMoreLinkElement.classList.remove('show-less-link');
            this.showMoreLinkElement.classList.add('show-more-link');
            this.showMoreLinkElement.innerHTML = this.showMoreText;
        }
    }

    private showAllSubvalues(): void {
        this.children.forEach(ch => show(ch.baseElement));
    }

    private showDefaultChildren(): void {
        this.children.forEach((ch, i) => {
            if (i < this.defaultChildNumberDisplayed) {
                show(ch.baseElement);
            } else {
                hide(ch.baseElement);
            }
        });
    }

    private hideSomeSubValues(): void {
        if (this.children.length <= this.defaultChildNumberDisplayed) {
            return;
        }
        const childrenToHide = this.children.slice(this.defaultChildNumberDisplayed);
        childrenToHide.forEach(ch => hide(ch.baseElement));
    }
}
